/**
 * Secure Token Manager
 * 
 * Provides secure storage and retrieval of JWT tokens using multiple security layers:
 * 1. Memory-based storage (primary)
 * 2. Encrypted sessionStorage (fallback)
 * 3. Token validation and expiry checking
 * 4. Automatic cleanup on security violations
 */

// In-memory token storage (most secure - not accessible via XSS)
let memoryTokenStore = {
  accessToken: null,
  refreshToken: null,
  tokenExpiry: null,
  lastActivity: Date.now()
};

// Simple encryption/decryption for sessionStorage (obfuscation layer)
const STORAGE_KEY = 'pari_auth_session';
const ENCRYPTION_KEY = 'pari_secure_2024'; // In production, this should be more complex

const simpleEncrypt = (text) => {
  if (!text) return '';
  
  try {
    // Simple XOR encryption for obfuscation
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length)
      );
    }
    return btoa(result); // Base64 encode
  } catch (error) {
    console.error('Encryption error:', error);
    return '';
  }
};

const simpleDecrypt = (encryptedText) => {
  if (!encryptedText) return '';
  
  try {
    const decoded = atob(encryptedText); // Base64 decode
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(
        decoded.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length)
      );
    }
    return result;
  } catch (error) {
    console.error('Decryption error:', error);
    return '';
  }
};

// Validate JWT token structure and expiry
const validateToken = (token) => {
  if (!token || typeof token !== 'string') {
    return { valid: false, reason: 'Invalid token format' };
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return { valid: false, reason: 'Invalid JWT structure' };
    }

    // Decode payload to check expiry
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < currentTime) {
      return { valid: false, reason: 'Token expired' };
    }

    return { valid: true, payload };
  } catch (error) {
    return { valid: false, reason: 'Token parsing error' };
  }
};

// Store tokens securely in sessionStorage as encrypted backup
const storeInSession = (accessToken, refreshToken) => {
  try {
    const tokenData = {
      accessToken: simpleEncrypt(accessToken || ''),
      refreshToken: simpleEncrypt(refreshToken || ''),
      timestamp: Date.now(),
      checksum: btoa(accessToken + refreshToken + Date.now()) // Integrity check
    };
    
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokenData));
  } catch (error) {
    console.error('Failed to store tokens in session:', error);
  }
};

// Retrieve tokens from sessionStorage
const retrieveFromSession = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const tokenData = JSON.parse(stored);
    
    // Check if data is too old (24 hours max)
    if (Date.now() - tokenData.timestamp > 24 * 60 * 60 * 1000) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      accessToken: simpleDecrypt(tokenData.accessToken),
      refreshToken: simpleDecrypt(tokenData.refreshToken)
    };
  } catch (error) {
    console.error('Failed to retrieve tokens from session:', error);
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

// Clean up all token storage
const clearAllStorage = () => {
  // Clear memory
  memoryTokenStore = {
    accessToken: null,
    refreshToken: null,
    tokenExpiry: null,
    lastActivity: Date.now()
  };
  
  // Clear sessionStorage
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear session storage:', error);
  }
  
  // Clear any remaining localStorage tokens (cleanup from old implementation)
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('type');
    localStorage.removeItem('userInfo');
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
  }
};

// Secure Token Manager API
export const secureTokenManager = {
  // Store tokens securely
  setTokens: (accessToken, refreshToken) => {
    // Validate tokens before storing
    const accessTokenValidation = validateToken(accessToken);
    if (!accessTokenValidation.valid) {
      console.error('Invalid access token:', accessTokenValidation.reason);
      return false;
    }

    // Store in memory (primary storage)
    memoryTokenStore.accessToken = accessToken;
    memoryTokenStore.refreshToken = refreshToken;
    memoryTokenStore.lastActivity = Date.now();
    
    // Calculate token expiry
    if (accessTokenValidation.payload?.exp) {
      memoryTokenStore.tokenExpiry = accessTokenValidation.payload.exp * 1000;
    }
    
    // Store encrypted backup in sessionStorage
    storeInSession(accessToken, refreshToken);

    return true;
  },

  // Get access token
  getAccessToken: () => {
    // Update last activity
    memoryTokenStore.lastActivity = Date.now();
    
    // Try memory first
    if (memoryTokenStore.accessToken) {
      const validation = validateToken(memoryTokenStore.accessToken);
      if (validation.valid) {
        return memoryTokenStore.accessToken;
      } else {
        console.warn('Memory token invalid:', validation.reason);
        memoryTokenStore.accessToken = null;
      }
    }
    
    // Fallback to sessionStorage
    const sessionTokens = retrieveFromSession();
    if (sessionTokens?.accessToken) {
      const validation = validateToken(sessionTokens.accessToken);
      if (validation.valid) {
        // Restore to memory
        memoryTokenStore.accessToken = sessionTokens.accessToken;
        memoryTokenStore.refreshToken = sessionTokens.refreshToken;
        return sessionTokens.accessToken;
      }
    }
    
    return null;
  },

  // Get refresh token
  getRefreshToken: () => {
    memoryTokenStore.lastActivity = Date.now();
    
    if (memoryTokenStore.refreshToken) {
      return memoryTokenStore.refreshToken;
    }
    
    // Fallback to sessionStorage
    const sessionTokens = retrieveFromSession();
    if (sessionTokens?.refreshToken) {
      memoryTokenStore.refreshToken = sessionTokens.refreshToken;
      return sessionTokens.refreshToken;
    }
    
    return null;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = secureTokenManager.getAccessToken();
    return token !== null;
  },

  // Get token expiry time
  getTokenExpiry: () => {
    return memoryTokenStore.tokenExpiry;
  },

  // Check if token is expired
  isTokenExpired: () => {
    const token = secureTokenManager.getAccessToken();
    if (!token) return true;
    
    const validation = validateToken(token);
    return !validation.valid;
  },

  // Clear all tokens
  clearTokens: () => {
    clearAllStorage();
  },

  // Get user info from token
  getUserInfo: () => {
    const token = secureTokenManager.getAccessToken();
    if (!token) return null;
    
    const validation = validateToken(token);
    if (validation.valid && validation.payload) {
      return {
        userId: validation.payload.sub || validation.payload.id,
        email: validation.payload.email,
        role: validation.payload.role || validation.payload.userType || validation.payload.type,
        exp: validation.payload.exp,
        iat: validation.payload.iat
      };
    }
    
    return null;
  },

  // Security check - detect if tokens might be compromised
  performSecurityCheck: () => {
    const now = Date.now();
    const timeSinceLastActivity = now - memoryTokenStore.lastActivity;
    
    // If no activity for more than 8 hours, clear tokens
    if (timeSinceLastActivity > 8 * 60 * 60 * 1000) {
      console.warn('Security check: Long inactivity detected, clearing tokens');
      clearAllStorage();
      return false;
    }
    
    // Check if token is still valid
    const token = memoryTokenStore.accessToken;
    if (token) {
      const validation = validateToken(token);
      if (!validation.valid) {
        console.warn('Security check: Invalid token detected, clearing tokens');
        clearAllStorage();
        return false;
      }
    }
    
    return true;
  },

  // Migration helper - move tokens from localStorage to secure storage
  migrateFromLocalStorage: () => {
    try {
      const oldToken = localStorage.getItem('token');
      const oldRefreshToken = localStorage.getItem('refreshToken');
      
      if (oldToken && oldRefreshToken) {
        
        const success = secureTokenManager.setTokens(oldToken, oldRefreshToken);
        if (success) {
          // Clear old storage
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }
    } catch (error) {
      console.error('Token migration failed:', error);
    }
  }
};

// Initialize security checks on page load
if (typeof window !== 'undefined') {
  // Migrate existing tokens
  secureTokenManager.migrateFromLocalStorage();
  
  // Set up periodic security checks
  setInterval(() => {
    secureTokenManager.performSecurityCheck();
  }, 5 * 60 * 1000); // Every 5 minutes
  
  // Clear tokens on page unload (security measure)
  window.addEventListener('beforeunload', () => {
    // Don't clear on refresh, only on actual navigation away
    if (performance.navigation.type !== performance.navigation.TYPE_RELOAD) {
      clearAllStorage();
    }
  });
}

export default secureTokenManager; 